/* eslint-disable class-methods-use-this, no-console */
/**
 * Facebook/Meta Pixel Helper
 * Wraps fbq for Facebook Pixel tracking
 * Supports dual tracking: Browser Pixel + Server-Side CAPI
 */
export class FacebookPixelHelper {
  constructor({ pixelId }) {
    this.pixelId = pixelId;
    this.initialized = false;
    this.capiEndpoint = '/analytics/facebook/capi';
  }

  init() {
    if (this.initialized || !window.fbq) return;
    this.initialized = true;
  }

  /**
   * Send event to server-side CAPI
   * Server will handle if token is configured
   * @private
   */
  async sendToCapi(eventName, data = {}) {
    try {
      // Get consent status from landing page
      const consent = this.getConsent();

      const payload = {
        event_name: eventName,
        url: window.location.href,
        referrer: document.referrer,
        consent_given: consent.marketing === true,
        custom_data: data,
        // Pass Facebook cookies for attribution
        fbc: this.getFbc(),
        fbp: this.getFbp(),
      };

      await fetch(this.capiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken(),
        },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      // Silent fail - don't block user experience
      console.warn('[FB CAPI] Failed to send server-side event:', e);
    }
  }

  /**
   * Get consent from landing page localStorage
   * @private
   */
  getConsent() {
    // If SKIP_ANALYTICS_CONSENT is set, grant all consent
    if (window.skipAnalyticsConsent) {
      return { necessary: true, analytics: true, marketing: true };
    }
    try {
      const saved = localStorage.getItem('ruut_cookie_consent_v1');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      // Ignore malformed consent payloads
    }
    return { necessary: true, analytics: false, marketing: false };
  }

  /**
   * Get CSRF token for authenticated requests
   * @private
   */
  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : '';
  }

  /**
   * Get Facebook Click ID (FBC) from URL or cookie
   * @private
   */
  getFbc() {
    // Check URL for fbclid
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');
    if (fbclid) {
      return `fb.1.${Date.now()}.${fbclid}`;
    }

    // Check cookie
    const match = document.cookie.match(/_fbc=([^;]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get Facebook Browser ID (FBP) from cookie
   * @private
   */
  getFbp() {
    const match = document.cookie.match(/_fbp=([^;]+)/);
    return match ? match[1] : null;
  }

  /**
   * Identify user in Facebook Pixel
   * Uses Advanced Matching for better attribution
   * @param {Object} user - User object with email, name, etc.
   */
  identify(user) {
    if (!window.fbq) return;

    // Advanced matching - hash PII for privacy
    const advancedMatching = {};
    if (user.email) advancedMatching.em = user.email.toLowerCase().trim();
    if (user.name) {
      const nameParts = user.name.split(' ');
      advancedMatching.fn = nameParts[0];
      advancedMatching.ln = nameParts.slice(1).join(' ');
    }

    // Re-initialize with advanced matching
    window.fbq('init', this.pixelId, advancedMatching);
  }

  /**
   * Track a standard or custom event
   * @param {string} eventName - Facebook event name (e.g., 'PageView', 'CompleteRegistration')
   * @param {Object} properties - Event parameters
   */
  track(eventName, properties = {}) {
    if (!window.fbq) return;

    // For custom events, use 'trackCustom'
    if (eventName === 'CustomEvent') {
      window.fbq('trackCustom', 'RuutEvent', properties);
    } else {
      window.fbq('track', eventName, properties);
    }

    // Also send to CAPI for server-side tracking
    this.sendToCapi(eventName, properties);
  }

  /**
   * Track page view
   * @param {string} pageName - Page identifier
   * @param {Object} properties - Additional page data
   */
  page(pageName, properties = {}) {
    if (!window.fbq) return;

    window.fbq('track', 'PageView', {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      ...properties,
    });
  }

  /**
   * Track purchase/subscription conversion
   * @param {Object} data - Purchase data
   */
  trackPurchase(data = {}) {
    if (!window.fbq) return;

    const eventData = {
      currency: data.currency || 'NGN',
      value: data.value || 0,
      content_type: 'product',
      content_ids: data.productIds || [data.planName || 'subscription'],
      subscription_id: data.subscriptionId,
      plan_name: data.planName,
      ...data,
    };

    window.fbq('track', 'Purchase', eventData);

    // Also send to CAPI
    this.sendToCapi('Purchase', eventData);
  }

  /**
   * Track trial start
   * @param {Object} data - Trial data
   */
  trackTrialStart(data = {}) {
    if (!window.fbq) return;

    const eventData = {
      currency: data.currency || 'NGN',
      predicted_ltv: data.predictedLtv,
      plan_name: data.planName,
      ...data,
    };

    window.fbq('track', 'StartTrial', eventData);

    // Also send to CAPI
    this.sendToCapi('StartTrial', eventData);
  }

  /**
   * Track subscription start
   * @param {Object} data - Subscription data
   */
  trackSubscribe(data = {}) {
    if (!window.fbq) return;

    const eventData = {
      currency: data.currency || 'NGN',
      value: data.value || 0,
      subscription_id: data.subscriptionId,
      plan_name: data.planName,
      ...data,
    };

    window.fbq('track', 'Subscribe', eventData);

    // Also send to CAPI
    this.sendToCapi('Subscribe', eventData);
  }

  /**
   * Track lead generation (for signup starts)
   * @param {Object} data - Lead data
   */
  trackLead(data = {}) {
    if (!window.fbq) return;

    const eventData = {
      content_name: data.contentName || 'Signup Start',
      ...data,
    };

    window.fbq('track', 'Lead', eventData);

    // Also send to CAPI
    this.sendToCapi('Lead', eventData);
  }

  /**
   * Update Facebook Pixel consent
   * @param {boolean} marketingAllowed - Whether marketing consent is granted
   */
  updateConsent(marketingAllowed) {
    if (!window.fbq) return;

    if (marketingAllowed) {
      window.fbq('consent', 'grant');
    } else {
      window.fbq('consent', 'revoke');
    }
  }
}
